import React from 'react';

/**
 * AppShellSkeleton — full-screen layout skeleton shown while the App.js
 * `bootstrap` call is in flight. Replaces the previous blank-page-with-
 * tiny-spinner loading state. The user sees the brand identity and the
 * familiar nav silhouette immediately, so cold-load no longer feels like
 * a broken page.
 *
 * Designed to visually match DashboardLayout closely so the transition
 * to the real shell is seamless. We don't render skeleton text under
 * the brand because we don't yet know if the user is internal or portal.
 */
const NAV_PLACEHOLDERS = [
  { width: 'w-20', section: true },
  { width: 'w-24' },
  { width: 'w-16' },
  { width: 'w-20' },
  { width: 'w-14', section: true },
  { width: 'w-20' },
  { width: 'w-24' },
  { width: 'w-20' },
  { width: 'w-24', section: true },
  { width: 'w-28' },
  { width: 'w-32' },
  { width: 'w-24' },
];

const AppShellSkeleton = () => (
  <div className="min-h-screen bg-[#FDFBF7] flex" data-testid="app-shell-skeleton">
    {/* Sidebar */}
    <aside className="w-64 border-r border-[#EDE4D6] bg-white/50 flex flex-col py-5 px-4 hidden md:flex">
      {/* Brand */}
      <div className="px-2 mb-6">
        <p className="text-[15px] tracking-tight">
          <span className="font-heading font-semibold text-[#222]">KEWL</span>
          <span className="text-[#A89685] text-[11px] tracking-[0.2em] ml-1">EVENT SOLUTIONS</span>
        </p>
      </div>

      {/* Search bar placeholder */}
      <div className="h-9 rounded-xl bg-[#F9F6F0] mb-5" />

      {/* Nav skeleton */}
      <div className="flex-1 space-y-2.5">
        {NAV_PLACEHOLDERS.map((p, i) => (
          <div key={i} className={p.section ? 'pt-3' : ''}>
            <div className={`h-3 ${p.width} rounded-md bg-[#EDE4D6]/70 ${p.section ? '' : 'ml-3'} animate-pulse`} style={{ animationDelay: `${i * 40}ms` }} />
          </div>
        ))}
      </div>

      {/* User chip at bottom */}
      <div className="pt-3 border-t border-[#EDE4D6] mt-3 flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-[#EDE4D6] animate-pulse" />
        <div className="space-y-1.5 flex-1">
          <div className="h-2.5 w-16 rounded-md bg-[#EDE4D6] animate-pulse" />
          <div className="h-2 w-12 rounded-md bg-[#F9F6F0] animate-pulse" />
        </div>
      </div>
    </aside>

    {/* Main pane */}
    <main className="flex-1 flex flex-col">
      {/* Top bar (mobile + bell area) */}
      <div className="md:hidden h-14 border-b border-[#EDE4D6] px-4 flex items-center justify-between">
        <p className="text-[15px] tracking-tight">
          <span className="font-heading font-semibold text-[#222]">KEWL</span>
          <span className="text-[#A89685] text-[10px] tracking-[0.2em] ml-1">EVENT SOLUTIONS</span>
        </p>
        <div className="w-7 h-7 rounded-full bg-[#EDE4D6] animate-pulse" />
      </div>

      {/* Page content area */}
      <div className="flex-1 p-6 md:p-8">
        {/* Title */}
        <div className="space-y-2 mb-6">
          <div className="h-9 w-48 rounded-md bg-[#EDE4D6]/70 animate-pulse" />
          <div className="h-3 w-72 rounded-md bg-[#F9F6F0] animate-pulse" />
        </div>

        {/* Card row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl border border-[#EDE4D6]/60 bg-white/40 p-5 space-y-3">
              <div className="h-3 w-20 rounded-md bg-[#F9F6F0] animate-pulse" />
              <div className="h-7 w-24 rounded-md bg-[#EDE4D6]/70 animate-pulse" />
            </div>
          ))}
        </div>

        {/* Hero block */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-2xl border border-[#EDE4D6]/60 bg-white/40 p-6 space-y-4">
            <div className="h-4 w-40 rounded-md bg-[#EDE4D6]/70 animate-pulse" />
            <div className="h-44 w-full rounded-xl bg-[#F9F6F0] animate-pulse" />
          </div>
          <div className="rounded-2xl border border-[#EDE4D6]/60 bg-white/40 p-6 space-y-3">
            <div className="h-4 w-32 rounded-md bg-[#EDE4D6]/70 animate-pulse" />
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-[#F9F6F0] animate-pulse flex-shrink-0" />
                <div className="flex-1 space-y-1.5 min-w-0">
                  <div className="h-3 w-full rounded-md bg-[#F9F6F0] animate-pulse" />
                  <div className="h-2.5 w-2/3 rounded-md bg-[#F9F6F0] animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  </div>
);

export default AppShellSkeleton;
